import { FC, useMemo } from 'react';
import { Button } from '@hyperflake/react-ui-library';
import { ICamera, ICameraCCTV } from '@api/models';

type CameraMapControlsProps = {
    selectedCamera: ICamera | ICameraCCTV;

    cameraAngle: number;
    cameraRange: number;
    cameraFOV: number;
    setCameraAngle: (v: number) => void;
    setCameraRange: (v: number) => void;
    setCameraFOV: (v: number) => void;

    // location editing
    isEditingLocation: boolean;
    tempLatitude: string;
    tempLongitude: string;
    setTempLatitude: (v: string) => void;
    setTempLongitude: (v: string) => void;

    onToggleLocationEdit: () => void; // Change Location / Apply Location
    onCancelLocationEdit: () => void; // Cancel

    onClose: () => void;

    onGetCurrent: () => void;
    onSave: () => void;

    isSaving?: boolean;

    // optional UX
    showUnsavedChanges?: boolean;
    setMapPreference?: () => void;
};

const CameraMapControls = (props: CameraMapControlsProps) => {
    const {
        selectedCamera,
        cameraAngle,
        cameraRange,
        cameraFOV,
        setCameraAngle,
        setCameraRange,
        setCameraFOV,
        isEditingLocation,
        tempLatitude,
        tempLongitude,
        setTempLatitude,
        setTempLongitude,
        onToggleLocationEdit,
        onCancelLocationEdit,
        onClose,
        onGetCurrent,
        onSave,
        isSaving = false,
        showUnsavedChanges = false,
        setMapPreference,
    } = props;

    const isTempCoordsValid = useMemo(() => {
        if (!isEditingLocation) return true;
        const lat = parseFloat(tempLatitude);
        const lng = parseFloat(tempLongitude);
        return !Number.isNaN(lat) && !Number.isNaN(lng);
    }, [isEditingLocation, tempLatitude, tempLongitude]);

    return (
        <div className="absolute top-6 right-6 z-10 bg-white rounded-lg shadow-lg p-4 w-80 max-h-[calc(100%-3rem)] overflow-y-auto">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">Camera View Setting</h3>

                <button onClick={onClose} className="text-gray-500 hover:text-gray-700 transition-colors">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <div className="pb-4 ">
                <p className="text-base font-semibold text-gray-900">{selectedCamera.name}</p>
            </div>

            {/* Location Controls */}
            <div className="mb-4 pt-4 border-t border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">Camera Location</label>

                {isEditingLocation && (
                    <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <p className="text-xs text-blue-800 flex items-start gap-2">
                            <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fillRule="evenodd"
                                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                    clipRule="evenodd"
                                />
                            </svg>
                            <span>
                                Click anywhere on the map to select a new location. Click &quot;Save&quot; to persist.
                            </span>
                        </p>
                    </div>
                )}

                <div className="flex justify-between gap-1">
                    <div className="mb-3">
                        <label className="block text-xs text-gray-600 mb-1">Latitude</label>
                        <input
                            type="number"
                            step="any"
                            value={tempLatitude}
                            onChange={(e) => setTempLatitude(e.target.value)}
                            disabled={!isEditingLocation}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder="e.g., 25.276987"
                        />
                    </div>

                    <div className="mb-3">
                        <label className="block text-xs text-gray-600 mb-1">Longitude</label>
                        <input
                            type="number"
                            step="any"
                            value={tempLongitude}
                            onChange={(e) => setTempLongitude(e.target.value)}
                            disabled={!isEditingLocation}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder="e.g., 55.296249"
                        />
                    </div>
                </div>

                {!isTempCoordsValid && (
                    <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded-md">
                        <p className="text-xs text-red-800">Please enter valid latitude &amp; longitude.</p>
                    </div>
                )}

                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={onToggleLocationEdit}
                        className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                            isEditingLocation
                                ? 'bg-orange-600 hover:bg-orange-700 text-white'
                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                    >
                        {isEditingLocation ? 'Apply Location' : 'Change Location'}
                    </button>

                    {isEditingLocation && (
                        <button
                            type="button"
                            onClick={onCancelLocationEdit}
                            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-md text-sm font-medium transition-colors"
                        >
                            Cancel
                        </button>
                    )}
                </div>
            </div>

            {showUnsavedChanges && (
                <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-xs text-yellow-800 flex items-center gap-2">
                        <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path
                                fillRule="evenodd"
                                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                clipRule="evenodd"
                            />
                        </svg>
                        Unsaved changes
                    </p>
                </div>
            )}

            {/* Angle */}
            <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Direction Angle: {cameraAngle}°</label>
                <input
                    type="range"
                    min="0"
                    max="360"
                    value={cameraAngle}
                    onChange={(e) => setCameraAngle(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>N (0°)</span>
                    <span>E (90°)</span>
                    <span>S (180°)</span>
                    <span>W (270°)</span>
                </div>
            </div>

            {/* Range */}
            <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Range: {cameraRange}m</label>
                <input
                    type="range"
                    min="10"
                    max="500"
                    step="10"
                    value={cameraRange}
                    onChange={(e) => setCameraRange(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>10m</span>
                    <span>500m</span>
                </div>
            </div>

            {/* FOV */}
            <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Field of View: {cameraFOV}°</label>
                <input
                    type="range"
                    min="30"
                    max="180"
                    step="10"
                    value={cameraFOV}
                    onChange={(e) => setCameraFOV(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>30°</span>
                    <span>180°</span>
                </div>
            </div>

            {/* Actions */}
            {/* Actions */}
            <div className="flex gap-2 pt-4 border-t">
                <Button
                    onClick={onGetCurrent}
                    className="flex-1 px-4 py-2 bg-gray-500 text-sm rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSaving}
                >
                    Reset
                </Button>

                <Button
                    onClick={onSave}
                    className="flex-1 px-4 py-2 bg-primary text-sm rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSaving || (isEditingLocation && !isTempCoordsValid)}
                >
                    {isSaving ? 'Saving...' : 'Save'}
                </Button>
            </div>
            <Button
                onClick={() => setMapPreference?.()}
                className="flex-1 px-4 py-2 bg-blue-600 text-sm rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed mt-2 w-full"
                disabled={!setMapPreference || isSaving || (isEditingLocation && !isTempCoordsValid)}
                title={!setMapPreference ? 'Not available' : 'Save current map zoom & center'}
            >
                Set Zoom
            </Button>

            {/* <div className="flex gap-2 pt-4 border-t">
                <Button
                    onClick={onGetCurrent}
                    className="flex-1 px-4 py-2 bg-gray-500 text-sm rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSaving}
                >
                    Reset
                </Button>

                <Button
                    onClick={onSave}
                    className="flex-1 px-4 py-2 bg-primary text-sm rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isSaving || (isEditingLocation && !isTempCoordsValid)}
                >
                    {isSaving ? 'Saving...' : 'Save'}
                </Button>
            </div> */}
        </div>
    );
};

export default CameraMapControls;
